import { useUser } from '@paintscout/ui'
import { isUserAssigned, isUserOwner } from '@paintscout/util/builder'
import type { Permission, QuoteDocument } from 'paintscout'
import { useMemo } from 'react'

// Quote related permissions hook
export const useQuotePermissions = (quote: QuoteDocument) => {
  const { hasPermissions, user } = useUser()

  if (!user || !hasPermissions) {
    // In customer-view
    return {
      permissionToCreateQuote: false,
      permissionToEditQuote: false,
      permissionToSendQuote: false,
      permissionToAcceptQuote: false,
      permissionToCreateInvoice: false,
      permissionToEditInvoice: false,
      permissionToEditAssignedInvoice: false,
      permissionToEditAllInvoice: false,
      permissionToSendInvoice: false,

      permissionToAddAdditionalWork: false,
      permissionToEditPresentationSection: false,
      permissionToEdit: false,

      permissionToAssignDocuments: false,
      permissionToAcceptPayments: false,
      permissionToCreateContact: false
    }
  }

  const isOwner = isUserOwner(quote, user.user_id)
  const isAssigned = isUserAssigned(quote, user.user_id)
  const isInvoice = quote?.is_invoice ?? false

  const permissionTo = (permAssigned: Permission, permAll: Permission) => {
    return isOwner || (isAssigned && hasPermissions([permAssigned])) || hasPermissions([permAll])
  }

  const permissionToCreateQuote = hasPermissions(['create:quotes'])
  const permissionToEditQuote = permissionTo('write:assigned-quotes', 'write:all-quotes')
  const permissionToSendQuote = permissionTo('send:assigned-quotes', 'send:all-quotes')
  const permissionToAcceptQuote = permissionTo('accept:assigned-quotes', 'accept:all-quotes')

  const permissionToCreateInvoice = hasPermissions(['create:invoices'])
  const permissionToEditInvoice = permissionTo('write:assigned-invoices', 'write:all-invoices')
  const permissionToSendInvoice = permissionTo('send:assigned-invoices', 'send:all-invoices')

  const permissionToEditAssignedInvoice = hasPermissions(['write:assigned-invoices'])
  const permissionToEditAllInvoice = hasPermissions(['write:all-invoices'])

  const permissionToAddAdditionalWork = permissionTo('additional-work:assigned-quotes', 'additional-work:all-quotes')
  const permissionToEditPresentationSection = permissionTo(
    'write-presentation-section:assigned-quotes',
    'write-presentation-section:all-quotes'
  )

  const permissionToEdit = isInvoice ? permissionToEditInvoice : permissionToEditQuote

  const permissionToAssignDocuments = permissionTo('assign:assigned-documents', 'assign:all-documents')
  const permissionToAcceptPayments = permissionTo('accept-payment:assigned-documents', 'accept-payment:all-documents')
  const permissionToCreateContact = hasPermissions(['create:contacts'])

  return useMemo(
    () => ({
      permissionToCreateQuote,
      permissionToEditQuote,
      permissionToSendQuote,
      permissionToAcceptQuote,
      permissionToCreateInvoice,
      permissionToEditInvoice,
      permissionToEditAssignedInvoice,
      permissionToEditAllInvoice,
      permissionToSendInvoice,

      permissionToAddAdditionalWork,
      permissionToEditPresentationSection,
      permissionToEdit,

      permissionToAssignDocuments,
      permissionToAcceptPayments,
      permissionToCreateContact
    }),
    [quote, user, hasPermissions]
  )
}
